import { motion } from "framer-motion";
import {
  Download,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Calendar,
  GraduationCap,
  Award,
  Code,
  Cloud,
  Database,
  Linkedin,
  Gitlab,
  Github,
  BarChart3,
} from "lucide-react";
import data from "../data/software.json";

const Resume = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <h1 className="font-display font-bold text-4xl md:text-5xl text-secondary-900 mb-4">
            Rohit N. Saigal
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Senior Software Developer - Full-stack engineer with 6+ years of
            experience
          </p>
        </motion.div>

        {/* Download Button */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <a
            href="/resume.pdf"
            download
            className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
          >
            <Download className="w-5 h-5" />
            <span>Download PDF Resume</span>
          </a>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="font-display font-semibold text-2xl text-secondary-900 mb-6">
            Contact Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-primary-600" />
              <span className="text-secondary-700">rohitnsaigal@gmail.com</span>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-primary-600" />
              <span className="text-secondary-700">(412) 477-2435</span>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-primary-600" />
              <span className="text-secondary-700">San Diego, CA</span>
            </div>
            <div className="flex items-center space-x-3">
              <Linkedin className="w-5 h-5 text-primary-600" />
              <a
                href="https://www.linkedin.com/in/rohit-saigal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-700 hover:text-primary-600 transition-colors duration-200"
              >
                linkedin.com/in/rohit-saigal
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <Gitlab className="w-5 h-5 text-primary-600" />
              <a
                href="https://gitlab.com/rohitnsaigal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-700 hover:text-primary-600 transition-colors duration-200"
              >
                gitlab.com/rohitnsaigal
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <Github className="w-5 h-5 text-primary-600" />
              <a
                href="https://github.com/rohitnsaigal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-700 hover:text-primary-600 transition-colors duration-200"
              >
                github.com/rohitnsaigal
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <ExternalLink className="w-5 h-5 text-primary-600" />
              <a
                href="https://portfolio.rohitsaigal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-700 hover:text-primary-600 transition-colors duration-200"
              >
                portfolio.rohitsaigal.com
              </a>
            </div>
          </div>
        </motion.div>

        {/* Summary */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="font-display font-semibold text-2xl text-secondary-900 mb-4">
            Professional Summary
          </h2>
          <p className="text-secondary-700 leading-relaxed">
            Full-stack engineer with 6+ years of experience designing and
            deploying secure, high-scale systems in cloud environments. Strong
            background in frontend and backend development, REST API design,
            infrastructure automation, and cloud architecture (AWS). Adept at
            refactoring legacy systems, leading migration strategies, and
            modernizing services with best practices and up-to-date frameworks.
            Passionate about solving complex engineering problems in meaningful
            domains such as health tech.
          </p>
        </motion.div>

        {/* Experience */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="font-display font-semibold text-2xl text-secondary-900 mb-6">
            Professional Experience
          </h2>
          <div className="space-y-6">
            {data["Professional Experience"].map((job, index) => (
              <div key={index} className="border-l-4 border-primary-500 pl-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-lg text-secondary-900">
                      {job.title}
                    </h3>
                    <p className="text-secondary-600 font-medium">
                      {job.company}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-secondary-500">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {formatDate(job.startDate)} - {formatDate(job.endDate)}
                    </span>
                  </div>
                </div>
                <ul className="space-y-2 mt-4">
                  {job.accomplishments.map((accomplishment, accIndex) => (
                    <li key={accIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-secondary-700 text-sm">
                        {accomplishment}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Projects */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="font-display font-semibold text-2xl text-secondary-900 mb-6">
            Projects
          </h2>
          <div className="space-y-6">
            {data["Projects"].map((project, index) => (
              <div key={index} className="border-l-4 border-accent-500 pl-6">
                <h3 className="font-semibold text-lg text-secondary-900 mb-2">
                  {project.title}
                </h3>
                <p className="text-secondary-600 text-sm mb-3">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="bg-accent-100 text-accent-700 px-2 py-1 rounded text-xs"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <ul className="space-y-1">
                  {project.highlights.map((highlight, highlightIndex) => (
                    <li
                      key={highlightIndex}
                      className="flex items-start space-x-2"
                    >
                      <div className="w-1.5 h-1.5 bg-accent-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-secondary-700 text-sm">
                        {highlight}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Skills */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="font-display font-semibold text-2xl text-secondary-900 mb-6">
            Technical Skills
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {Object.entries(data["Skills"]).map(([category, skills]) => (
              <div key={category}>
                <h3 className="font-semibold text-lg text-secondary-900 mb-3 flex items-center space-x-2">
                  {category === "Languages" && (
                    <Code className="w-5 h-5 text-primary-600" />
                  )}
                  {category === "Cloud & DevOps" && (
                    <Cloud className="w-5 h-5 text-primary-600" />
                  )}
                  {category === "CMS & Analytics" && (
                    <BarChart3 className="w-5 h-5 text-accent-600" />
                  )}
                  {category === "Security & Reliability" && (
                    <Award className="w-5 h-5 text-primary-600" />
                  )}
                  {category === "Frontend" && (
                    <Code className="w-5 h-5 text-accent-600" />
                  )}
                  {category === "Backend" && (
                    <Database className="w-5 h-5 text-accent-600" />
                  )}
                  {category === "Developer Tools" && (
                    <Award className="w-5 h-5 text-secondary-600" />
                  )}
                  <span>{category}</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, index) => (
                    <span
                      key={index}
                      className="bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full text-sm"
                    >
                      {skill.skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Education */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg p-8"
        >
          <h2 className="font-display font-semibold text-2xl text-secondary-900 mb-6">
            Education
          </h2>
          <div className="flex items-start space-x-4">
            <GraduationCap className="w-6 h-6 text-primary-600 mt-1" />
            <div>
              <h3 className="font-semibold text-lg text-secondary-900">
                {data["Education"].degree.name}
              </h3>
              <p className="text-secondary-600">
                {data["Education"].degree.degree} -{" "}
                {data["Education"].degree.subject}
              </p>
              <p className="text-secondary-500 text-sm">
                {data["Education"].degree.years} • Graduated{" "}
                {data["Education"].degree.graduatingYear}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Resume;
